import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import { ArrowLeft, Tag, Clock, Loader2, ChevronDown } from 'lucide-react';
import { getCategoryColor } from '../constants/categoryColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

function TagListPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSkip(0);
    setArticles([]);
    fetchInitial();
  }, [tag]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/articles`, { params: { tag, skip: 0, limit: PAGE_SIZE } }),
        axios.get(`${API}/articles/count`, { params: { tag } })
      ]);
      setArticles(listRes.data);
      setTotal(countRes.data.count);
      setSkip(listRes.data.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/articles`, {
        params: { tag, skip, limit: PAGE_SIZE }
      });
      setArticles(prev => [...prev, ...data]);
      setSkip(prev => prev + data.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = articles.length < total;

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="tag-list-page">
      <SEO
        title={`#${tag} — Tagged Stories`}
        description={`Latest crime news and investigations tagged with #${tag}. Browse ${total || 'all'} articles on Crime Newz.`}
        path={`/tag/${tag}`}
        type="website"
      />
      <Header />

      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <AdSlot slot="top" height={90} />
      </div>

      <section className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="mb-6">
          <Link
            to="/"
            data-testid="tag-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] hover:text-[#E63946] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Home
          </Link>
        </div>

        <div className="mb-10 pb-6 border-b-4 border-[#E63946]">
          <div className="flex items-center gap-3 mb-2">
            <Tag size={28} className="text-[#E63946]" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">TAG</span>
          </div>
          <h1
            data-testid="tag-page-title"
            className="text-4xl sm:text-5xl tracking-tight leading-none font-black text-[#1A1A2E]"
          >
            #{tag}
          </h1>
          {!loading && (
            <p className="text-sm text-[#525252] mt-3" data-testid="tag-page-count">
              {total} {total === 1 ? 'article' : 'articles'} tagged with{' '}
              <span className="font-semibold text-[#1A1A2E]">#{tag}</span>
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={32} className="animate-spin text-[#E63946]" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-24" data-testid="tag-empty-state">
            <Tag size={48} className="text-[#D5D5D5] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1A1A2E] mb-2">No articles tagged "#{tag}"</h2>
            <p className="text-[#525252] mb-6">Try a different tag or browse the homepage.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e]"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tag-articles-grid">
              {articles.map((article, idx) => {
                const c = getCategoryColor(article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    data-testid={`tag-article-${idx}`}
                    className="group bg-white border border-[#E5E5E5] hover:border-[#E63946] overflow-hidden transition-colors"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-[0.2em] uppercase mb-2"
                        style={{ backgroundColor: c.tint, color: c.bg }}
                      >
                        {c.name}
                      </span>
                      <h3 className="text-base font-bold text-[#1A1A2E] leading-snug group-hover:text-[#E63946] transition-colors mb-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-[#8A8A8A]">
                        <Clock size={12} />
                        {new Date(article.published_at).toLocaleDateString()}
                        <span>•</span>
                        <span>{article.author}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="tag-load-more"
                  className="flex items-center gap-2 px-8 py-3 border-2 border-[#E63946] text-[#E63946] font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#E63946] hover:text-white transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      LOADING...
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      LOAD MORE
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <div className="max-w-[1600px] mx-auto px-6 pb-6">
        <AdSlot slot="bottom" height={90} />
      </div>

      <Footer />
    </div>
  );
}

export default TagListPage;
